// src/components/Layout/TopBar.js

import React, { useEffect, useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Tooltip,
  Button,
  useMediaQuery,
  Badge,
  Popover,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Moon,
  Sun,
  LogOut,
  Bell,
  Sparkles,
  Settings,
} from "lucide-react";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../context/AuthContext";
import useAuthHook from "../../hooks/useAuth";
import { useLocation } from "react-router-dom";
import API from "../../utils/api";
import TopBarHelpMenu from "../../guidedTour/TopBarHelpMenu";
import ReadOnlyBadge from "../ReadOnlyBadge";

export default function TopBar({ onMenuClick }) {
  const theme = useTheme();
  const { logout } = useAuth();
  const { isReadOnly } = useAuthHook();

  // 📌 Correct mobile & landscape detection
  const isMobileWidth = useMediaQuery(theme.breakpoints.down("lg"));
  const isShortHeight = useMediaQuery("(max-height: 500px)");
  const isMobile = isMobileWidth || isShortHeight;
  const location = useLocation();
  const hideTopBarOnScroll = isMobile && location.pathname !== "/team-chat";

  // 🔽 ADD: scroll hide state (mobile only)
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);
  const [bellAnchorEl, setBellAnchorEl] = useState(null);
  const [whatsNewItems, setWhatsNewItems] = useState([]);
  const [whatsNewUnread, setWhatsNewUnread] = useState(0);
  const [companyLogoUrl, setCompanyLogoUrl] = useState("");

  // Keep the shell's reserved TopBar space synchronized on mobile.
  useEffect(() => {
    if (!isMobile) {
      document.documentElement.style.removeProperty("--topbar-height");
      return undefined;
    }

    document.documentElement.style.setProperty(
      "--topbar-height",
      hidden ? "0px" : "calc(64px + env(safe-area-inset-top, 0px))"
    );

    return () => {
      document.documentElement.style.removeProperty("--topbar-height");
    };
  }, [hidden, isMobile]);

  useEffect(() => {
    if (!hideTopBarOnScroll) {
      setHidden(false);
      return undefined;
    }

    const scrollEl = document.querySelector("main");
    if (!scrollEl) return undefined;

    lastScrollY.current = scrollEl.scrollTop;
    setHidden(false);

    const handleScroll = () => {
      const currentY = scrollEl.scrollTop;
      const delta = currentY - lastScrollY.current;
      const nearTop = currentY < 64;
      const nearBottom =
        scrollEl.scrollHeight - (currentY + scrollEl.clientHeight) < 8;

      if (Math.abs(delta) < 4) return;

      if (nearBottom) {
        lastScrollY.current = currentY;
        return;
      }

      setHidden(delta > 0 && !nearTop);
      lastScrollY.current = currentY;
    };

    scrollEl.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollEl.removeEventListener("scroll", handleScroll);
    };
  }, [hideTopBarOnScroll]);

  const isLight = theme.palette.mode === "light";
  const sidebarBg = isLight ? "#ffffff" : "#1e293b";
  const textColor = isLight ? "#1e293b" : "#ffffff";
  const borderColor = isLight ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.1)";
  const bellOpen = Boolean(bellAnchorEl);

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  const fetchWhatsNew = async () => {
    try {
      const [countRes, listRes] = await Promise.all([
        API.get("/notifications/unread_count/", { params: { type: "whats_new" } }),
        API.get("/notifications/", { params: { type: "whats_new", limit: 7 } }),
      ]);
      setWhatsNewUnread(countRes.data?.unread_count || 0);
      setWhatsNewItems(Array.isArray(listRes.data) ? listRes.data : []);
    } catch (err) {
      console.error("Failed to load What's New items:", err);
    }
  };

  const fetchCompanyLogo = async () => {
    try {
      const res = await API.get("/company-profile/");
      setCompanyLogoUrl(res.data?.logo_url || "");
    } catch (err) {
      console.error("Failed to load company logo:", err);
      setCompanyLogoUrl("");
    }
  };

  useEffect(() => {
    fetchWhatsNew();
    fetchCompanyLogo();
    const interval = setInterval(fetchWhatsNew, 60000);
    const onWhatsNewUpdated = () => fetchWhatsNew();
    const onCompanyProfileUpdated = () => fetchCompanyLogo();
    const onFocus = () => fetchWhatsNew();
    window.addEventListener("whatsNewUpdated", onWhatsNewUpdated);
    window.addEventListener("companyProfileUpdated", onCompanyProfileUpdated);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("whatsNewUpdated", onWhatsNewUpdated);
      window.removeEventListener("companyProfileUpdated", onCompanyProfileUpdated);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const handleBellOpen = async (event) => {
    setBellAnchorEl(event.currentTarget);
    try {
      await API.post("/notifications/mark-whats-new-read/");
      setWhatsNewUnread(0);
      setWhatsNewItems((prev) => prev.map((item) => ({ ...item, is_read: true })));
    } catch (err) {
      console.error("Failed to mark What's New read:", err);
    }
  };

  return (
    <AppBar
      id="app-topbar"
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        bgcolor: sidebarBg,
        color: textColor,
        borderBottom: `1px solid ${borderColor}`,
        boxShadow: isLight ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
        width: "100%",
        ml: 0,
        top: 0,
        left: 0,
        boxSizing: "border-box",
        pt: "env(safe-area-inset-top, 0px)",
        transform:
          hideTopBarOnScroll && hidden ? "translateY(-100%)" : "translateY(0)",
        transition: "transform 0.25s ease-in-out",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          position: "relative",
          minHeight: "64px !important",
          px: { xs: 2, lg: 2 },
        }}
      >
        {/* Left: hamburger (mobile only) + title */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pl: { xs: 0, lg: 2 },
            minWidth: 0,
            flex: { xs: "1 1 auto", lg: "0 0 auto" },
            overflow: "hidden",
          }}
        >
          {isMobile && (
            <IconButton onClick={onMenuClick} sx={{ color: textColor }}>
              <MenuIcon size={20} strokeWidth={1.75} />
            </IconButton>
          )}

          <Box
            component="img"
            src={
              companyLogoUrl ||
              (theme.palette.mode === "dark"
                ? "/topbar-logo-dark-bg.png"
                : "/topbar-logo-light-bg.png")
            }
            alt="Territory CRM"
            sx={{
              height: { xs: 30, lg: 40 },
              width: "auto",
              /* Mobile: keep logo beside menu; cap width so theme/bell/settings fit */
              maxWidth: { xs: "min(132px, 34vw)", sm: 200, lg: 260 },
              display: "block",
              objectFit: "contain",
              objectPosition: "left center",
              flexShrink: 1,
              minWidth: 0,
            }}
          />
          
          {/* ✅ Read Only badge next to title */}
          {isReadOnly && (
            <Box sx={{ ml: 1 }}>
              <ReadOnlyBadge />
            </Box>
          )}
        </Box>

        {/* Right side: help, theme, bell, logout */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
            ml: { xs: 0, lg: "-250px" },
          }}
        >
          {!isMobile && <TopBarHelpMenu />}

          <Tooltip
            title={`Switch to ${
              theme.palette.mode === "light" ? "dark" : "light"
            } mode`}
          >
            <IconButton
              data-tour="topbar-theme-mode"
              onClick={() =>
                theme.palette.mode === "light"
                  ? document.dispatchEvent(
                      new CustomEvent("themeToggle", { detail: "dark" })
                    )
                  : document.dispatchEvent(
                      new CustomEvent("themeToggle", { detail: "light" })
                    )
              }
              sx={{
                color: textColor,
                "&:hover": { bgcolor: isLight ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)" },
              }}
            >
              {theme.palette.mode === "light" ? (
                <Moon size={20} strokeWidth={1.75} />
              ) : (
                <Sun size={20} strokeWidth={1.75} />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="What's New">
            <IconButton
              data-tour="topbar-bell"
              onClick={handleBellOpen}
              sx={{
                color: textColor,
                "&:hover": { bgcolor: isLight ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)" },
              }}
            >
              <Badge
                badgeContent={whatsNewUnread > 9 ? "9+" : whatsNewUnread}
                color="error"
                overlap="circular"
                invisible={whatsNewUnread <= 0}
              >
                <Bell size={20} strokeWidth={1.75} />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Desktop Logout Button */}
          <Button
            startIcon={<LogOut size={18} strokeWidth={1.75} />}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              borderColor: isLight ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.3)",
              color: textColor,
              "&:hover": {
                borderColor: isLight ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.6)",
                backgroundColor: isLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.1)",
              },
              display: { xs: "none", lg: "flex" },
            }}
            onClick={logout}
          >
            Logout
          </Button>

          {/* Mobile Logout */}
          <IconButton
            onClick={logout}
            sx={{
              display: { xs: "flex", lg: "none" },
              color: textColor,
              "&:hover": { bgcolor: isLight ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)" },
            }}
          >
            <LogOut size={20} strokeWidth={1.75} />
          </IconButton>
        </Box>
      </Toolbar>

      <Popover
        open={bellOpen}
        anchorEl={bellAnchorEl}
        onClose={() => setBellAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: { xs: "min(92vw, 390px)", sm: 390 },
            borderRadius: 2,
            border: "1px solid",
            borderColor: isLight ? "rgba(15,23,42,0.08)" : "rgba(148,163,184,0.18)",
            boxShadow: "0 10px 30px rgba(2,6,23,0.12), 0 2px 8px rgba(2,6,23,0.08)",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.01em" }}>
            What's New
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
          {!whatsNewItems.length ? (
            <Typography sx={{ px: 2, py: 2, color: "text.secondary", fontSize: 14 }}>
              No new updates.
            </Typography>
          ) : (
            whatsNewItems.map((item, idx) => {
              const isFeature = (item.message || "").toLowerCase().includes("added");
              const Icon = isFeature ? Sparkles : Settings;
              const iconBg = isFeature ? "rgba(16,185,129,0.12)" : "rgba(59,130,246,0.12)";
              const iconColor = isFeature ? "#059669" : "#2563EB";
              return (
                <Box key={item.id || idx}>
                  <Stack direction="row" spacing={1.5} sx={{ px: 2, py: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: iconBg,
                        color: iconColor,
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      <Icon size={16} strokeWidth={1.75} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.35 }} noWrap>
                        {item.message?.split(":")[0] || "Release update"}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.25,
                          fontSize: 13.5,
                          color: "text.secondary",
                          lineHeight: 1.45,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.message?.includes(":")
                          ? item.message.split(":").slice(1).join(":").trim()
                          : item.message}
                      </Typography>
                      <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.disabled" }}>
                        {formatDate(item.created_at)}
                      </Typography>
                    </Box>
                  </Stack>
                  {idx < whatsNewItems.length - 1 && <Divider />}
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </AppBar>
  );
}
